import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/types/navigation';
import {
    Container,
    AttributeSection,
    Attribute,
    CircleButton,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    YesButton,
    NoButton,
    ResumeDialogPaperProps,
    PauseDialogPaperProps,
    ArchiveDialogPaperProps,
    PriorityLabelWrapper,
    InfoIconButton,
    StatusContent,
    DateContent,
    LlmAttributeContent,
    ViewSessionsButton,
    PlanningActions,
    DeleteQuestButton,
    MergeButton
} from './styled';
import {
    Title,
    PriorityDropdown,
    PriorityInfoModal,
    ConfirmationDialog,
    BeehiveReviewModal
} from 'src/shared';
import { QuestStatus, QuestActivityType } from 'src/enums/quests';
import { Tooltip, Dialog } from '@mui/material';
import { ReactComponent as InfoIcon } from 'src/assets/icons/information.svg';
import { dateAndTimeSeperatedString } from 'src/utils/dateFormat';
import useAppDispatch from 'src/hooks/useAppDispatch';
import { getQuestActivities, getQuest } from 'src/redux/quests/service';
import { QuestStatusMap } from 'src/types/client';
import {
    deleteDraftQuest,
    postAutoMergeQuest,
    updateQuest
} from 'src/api/quests';
import { showSnackbar } from 'src/utils/snackbar';
import { isAdmin } from '../../../../../redux/auth/service';
import QuestActionButton from 'src/pages/Quest/Review/components/ReviewButton';
import ReviewSplitButton from 'src/shared/ReviewSplitButton';
import { ReactComponent as PauseIcon } from 'src/assets/icons/pause.svg';
import { ReactComponent as ResumeIcon } from 'src/assets/icons/resume.svg';
import { ReactComponent as ArchiveIcon } from 'src/assets/icons/archive.svg';
import { ReactComponent as UnarchiveIcon } from 'src/assets/icons/unarchive.svg';
import { useAsyncJobPolling } from 'src/hooks/useAsyncJobPolling';
import {
    BEEHIVE_HUMAN_REVIEW_TEXT,
    QuestActivity,
    QuestSchema,
    resolveBeehiveHumanReviewStatus
} from 'src/types/quests';

interface WorkOverviewProps {
    quest: QuestSchema;
}

const MERGE_POLL_INTERVAL_MS = 3000;

const WorkOverview: FC<WorkOverviewProps> = ({ quest }) => {
    const admin = isAdmin();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [activities, setActivities] = useState<QuestActivity[]>([]);
    const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
    const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [urgentPriorityDialogOpen, setUrgentPriorityDialogOpen] =
        useState(false);
    const [priorityInfoModalOpen, setPriorityInfoModalOpen] = useState(false);
    const [archiveType, setArchiveType] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
    const [merging, setMerging] = useState(false);
    const [beehiveModalOpen, setBeehiveModalOpen] = useState(false);

    const handleMergeJobReady = useCallback(
        (job: { status?: string; result?: { status?: string } }) => {
            setMerging(false);
            if (job.status === 'failed') {
                showSnackbar(
                    dispatch,
                    'error',
                    'Auto-merge failed. Please review the PRs manually.'
                );
            } else if (job.result?.status === 'merged') {
                showSnackbar(
                    dispatch,
                    'success',
                    'Request merged successfully.'
                );
            } else if (job.result?.status === 'no_prs') {
                showSnackbar(
                    dispatch,
                    'error',
                    'No pull requests found to merge.'
                );
            } else if (job.result?.status === 'failed') {
                showSnackbar(
                    dispatch,
                    'error',
                    'Auto-merge could not complete. Please review the PRs manually.'
                );
            }
            dispatch(getQuest({ questId: quest.id }));
        },
        [dispatch, quest.id]
    );

    const handleMergeJobError = useCallback((error: unknown) => {
        console.error('Failed to poll merge job:', error);
        setMerging(false);
    }, []);

    const { startPolling: pollMergeJob } = useAsyncJobPolling({
        enabled: false,
        intervalMs: MERGE_POLL_INTERVAL_MS,
        onJobReady: handleMergeJobReady,
        onError: handleMergeJobError
    });

    useEffect(() => {
        if (quest.id) {
            dispatch(getQuestActivities({ questId: quest.id }))
                .unwrap()
                .then((fetchedActivities: QuestActivity[]) => {
                    setActivities(fetchedActivities);
                })
                .catch((error: any) => {
                    console.error('Failed to fetch quest activities:', error);
                });
        }
    }, [dispatch, quest.id]);

    const handleStatusChange = useCallback(
        async (status: QuestStatus) => {
            try {
                if (!quest.id) {
                    console.error('Quest ID is missing');
                    return;
                }
                await updateQuest(quest.id, { status: status });

                dispatch(getQuest({ questId: quest.id }));
            } catch (error) {
                console.error('Failed to update quest status:', error);
                console.error('Error details:', error);
            }
        },
        [quest.id, dispatch]
    );

    const handlePriorityChange = useCallback(
        async (priority: number, skipConfirmation: boolean = false) => {
            try {
                if (!quest.id) {
                    console.error('Quest ID is missing');
                    return;
                }

                // Special case: admin selecting priority 10 (urgent) - show confirmation dialog
                if (admin && priority === 10 && !skipConfirmation) {
                    setUrgentPriorityDialogOpen(true);
                    return;
                }

                // Close dialog if it was open
                if (skipConfirmation) {
                    setUrgentPriorityDialogOpen(false);
                }

                await updateQuest(quest.id, {
                    priority: priority,
                    status: undefined
                });
                dispatch(getQuest({ questId: quest.id }));
            } catch (error) {
                console.error('Failed to update quest priority:', error);
            }
        },
        [quest.id, dispatch, admin]
    );

    const handleCancelUrgentPriority = useCallback(() => {
        setUrgentPriorityDialogOpen(false);
    }, []);

    // Resume dialog handlers
    const handleResumeClick = useCallback(() => {
        setResumeDialogOpen(true);
    }, []);

    const handleConfirmResume = useCallback(() => {
        setResumeDialogOpen(false);
        handleStatusChange(QuestStatus.NEW);
    }, [handleStatusChange]);

    const handleCancelResume = useCallback(() => {
        setResumeDialogOpen(false);
    }, []);

    // Pause dialog handlers
    const handlePauseClick = useCallback(() => {
        setPauseDialogOpen(true);
    }, []);

    const handleConfirmPause = useCallback(() => {
        setPauseDialogOpen(false);
        handleStatusChange(QuestStatus.ON_HOLD);
    }, [handleStatusChange]);

    const handleCancelPause = useCallback(() => {
        setPauseDialogOpen(false);
    }, []);

    const handleArchiveClick = useCallback((type: string) => {
        setArchiveDialogOpen(true);
        setArchiveType(type);
    }, []);

    const handleConfirmArchive = useCallback(() => {
        setArchiveDialogOpen(false);
        if (archiveType === 'archive') {
            handleStatusChange(QuestStatus.ARCHIVED);
        } else if (archiveType === 'unarchive') {
            handleStatusChange(QuestStatus.UNARCHIVED);
        }
    }, [archiveType, handleStatusChange]);

    const handleCancelArchive = useCallback(() => {
        setArchiveDialogOpen(false);
    }, []);

    // IN_REVIEW and PLANNING get their own primary action button next to
    // the priority / status attributes (review the work, or open the
    // planning session). For every other status the action slot is empty
    // and the row just shows the pause / archive controls.
    const handleReviewClick = useCallback(() => {
        navigate(RoutesEnum.QUEST_REVIEW.replace(':id', quest.id));
    }, [navigate, quest.id]);

    const handleBeehiveReview = useCallback(() => {
        setBeehiveModalOpen(true);
    }, []);

    const handleBeehiveModalSuccess = useCallback(() => {
        dispatch(getQuest({ questId: quest.id }));
    }, [dispatch, quest.id]);

    const handleViewPlanningClick = useCallback(() => {
        if (quest.planningSessionId) {
            navigate(
                RoutesEnum.PLAN_QUEST_SESSION.replace(
                    ':sessionId',
                    quest.planningSessionId
                )
            );
        }
    }, [navigate, quest.planningSessionId]);

    const handleDeleteClick = useCallback(() => {
        setDeleteDialogOpen(true);
    }, []);

    const handleCancelDelete = useCallback(() => {
        setDeleteDialogOpen(false);
    }, []);

    const handleMergeClick = useCallback(() => {
        setMergeDialogOpen(true);
    }, []);

    const handleCancelMerge = useCallback(() => {
        setMergeDialogOpen(false);
    }, []);

    const handleConfirmMerge = useCallback(async () => {
        setMergeDialogOpen(false);
        if (!quest.id) {
            return;
        }
        setMerging(true);
        try {
            const response = await postAutoMergeQuest(quest.id);
            const jobId = response?.data?.jobId;
            showSnackbar(
                dispatch,
                'success',
                "We'll handle your merge request shortly."
            );
            if (jobId) {
                pollMergeJob(jobId);
            } else {
                setMerging(false);
            }
        } catch (error) {
            console.error('Failed to trigger auto-merge:', error);
            setMerging(false);
            showSnackbar(
                dispatch,
                'error',
                'Failed to trigger the merge. Please try again.'
            );
        }
    }, [dispatch, pollMergeJob, quest.id]);

    const handleConfirmDelete = useCallback(() => {
        if (!quest.id) {
            return;
        }
        setDeleting(true);
        deleteDraftQuest(quest.id)
            .then(() => {
                showSnackbar(
                    dispatch,
                    'success',
                    'Request deleted successfully'
                );
                navigate(RoutesEnum.CLIENT_DASHBOARD);
            })
            .catch(() => {
                showSnackbar(dispatch, 'error', 'Failed to delete request');
            })
            .finally(() => {
                setDeleting(false);
                setDeleteDialogOpen(false);
            });
    }, [quest.id, dispatch, navigate]);

    const isReviewable = quest.status === QuestStatus.IN_REVIEW;
    const isPlanning =
        quest.status === QuestStatus.PLANNING && !!quest.planningSessionId;
    const showPauseButton =
        quest.status === QuestStatus.IN_PROCESS ||
        quest.status === QuestStatus.NEW;
    const showResumeButton = quest.status === QuestStatus.ON_HOLD;
    const showStatusControls = !isPlanning;

    const SolutionApprovedActivity = useMemo(
        () =>
            activities.find(
                (activity: QuestActivity) =>
                    activity.activityType ===
                        QuestActivityType.STATUS_CHANGED &&
                    activity.details.statusChange?.to === QuestStatus.DONE
            ),
        [activities]
    );

    const dateEnded = useMemo(() => {
        return SolutionApprovedActivity
            ? SolutionApprovedActivity.created
            : null;
    }, [SolutionApprovedActivity]);

    return (
        <Container>
            <Title>Work Overview</Title>
            <AttributeSection>
                {quest.status !== QuestStatus.DONE ? (
                    <>
                        <Tooltip
                            title="The following is the count of iterations for this request"
                            placement="top"
                            arrow
                        >
                            <Attribute>
                                <label>Iterations</label>
                                <DateContent>
                                    {quest.iterations || 0}
                                </DateContent>
                            </Attribute>
                        </Tooltip>
                    </>
                ) : (
                    <>
                        <Attribute>
                            <label>Date Started</label>
                            <DateContent>
                                {String(
                                    dateAndTimeSeperatedString(quest.created)
                                        .date
                                )}
                            </DateContent>
                        </Attribute>
                        <Attribute>
                            <label>Date Ended</label>
                            <DateContent>
                                {dateEnded
                                    ? String(
                                          dateAndTimeSeperatedString(dateEnded)
                                              .date
                                      )
                                    : 'N/A'}
                            </DateContent>
                        </Attribute>
                    </>
                )}
                <Attribute>
                    <label>Status</label>
                    <StatusContent>
                        <span>
                            {merging
                                ? 'Merging…'
                                : quest.isMerged
                                  ? 'Merged'
                                  : QuestStatusMap[quest.status]}
                        </span>
                        {showStatusControls && showPauseButton && (
                            <Tooltip
                                title="Pause request"
                                placement="top"
                                arrow
                            >
                                <CircleButton onClick={handlePauseClick}>
                                    <PauseIcon />
                                </CircleButton>
                            </Tooltip>
                        )}
                        {showStatusControls && showResumeButton && (
                            <Tooltip
                                title="Resume request"
                                placement="top"
                                arrow
                            >
                                <CircleButton onClick={handleResumeClick}>
                                    <ResumeIcon />
                                </CircleButton>
                            </Tooltip>
                        )}
                        {showStatusControls && admin && (
                            <Tooltip
                                title={`${
                                    quest.status === QuestStatus.ARCHIVED
                                        ? 'Unarchive'
                                        : 'Archive'
                                } request`}
                                placement="top"
                                arrow
                            >
                                <CircleButton
                                    onClick={() =>
                                        handleArchiveClick(
                                            quest.status ===
                                                QuestStatus.ARCHIVED
                                                ? 'unarchive'
                                                : 'archive'
                                        )
                                    }
                                >
                                    {quest.status === QuestStatus.ARCHIVED ? (
                                        <UnarchiveIcon />
                                    ) : (
                                        <ArchiveIcon />
                                    )}
                                </CircleButton>
                            </Tooltip>
                        )}
                    </StatusContent>
                </Attribute>
                <Attribute>
                    <PriorityLabelWrapper>
                        Priority
                        {admin && (
                            <InfoIconButton
                                onClick={() => setPriorityInfoModalOpen(true)}
                                type="button"
                            >
                                <InfoIcon />
                            </InfoIconButton>
                        )}
                    </PriorityLabelWrapper>
                    <PriorityDropdown
                        priority={quest.priority}
                        onPriorityChange={handlePriorityChange}
                        isAdmin={admin}
                        disabled={
                            quest.status !== QuestStatus.ON_HOLD &&
                            quest.status !== QuestStatus.NEW &&
                            quest.status !== QuestStatus.IN_PROCESS
                        }
                        showLabel={false}
                    />
                </Attribute>
                {admin && (
                    <>
                        <Attribute>
                            <label>Contributor Cost</label>
                            <LlmAttributeContent>
                                ${Number(quest.contributorCostUsd).toFixed(2)}
                            </LlmAttributeContent>
                        </Attribute>
                        <Attribute>
                            <label>LLM Cost</label>
                            <LlmAttributeContent>
                                ${Number(quest.llmCostUsd).toFixed(2)}
                            </LlmAttributeContent>
                        </Attribute>
                        <Attribute>
                            <label>Total Cost</label>
                            <LlmAttributeContent>
                                $
                                {(
                                    Number(quest.contributorCostUsd) +
                                    Number(quest.llmCostUsd)
                                ).toFixed(2)}
                            </LlmAttributeContent>
                        </Attribute>
                        <Attribute>
                            <label>LLM Info</label>
                            <LlmAttributeContent>
                                <ViewSessionsButton
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                        navigate(
                                            RoutesEnum.QUEST_LLM_INFO.replace(
                                                ':id',
                                                quest.id
                                            )
                                        )
                                    }
                                >
                                    View Sessions
                                </ViewSessionsButton>
                            </LlmAttributeContent>
                        </Attribute>
                    </>
                )}
                <Attribute>
                    <label>Expert Review</label>
                    <DateContent>
                        {
                            BEEHIVE_HUMAN_REVIEW_TEXT[
                                resolveBeehiveHumanReviewStatus(
                                    quest.beehiveHumanReviewStatus
                                )
                            ]
                        }
                    </DateContent>
                </Attribute>
                {quest.status === QuestStatus.DONE &&
                    !quest.isMerged &&
                    !merging && (
                        <MergeButton
                            variant="contained"
                            size="small"
                            onClick={handleMergeClick}
                        >
                            Merge request
                        </MergeButton>
                    )}
                {isReviewable && (
                    <Attribute>
                        <label>&nbsp;</label>
                        <ReviewSplitButton
                            onReviewRequest={handleReviewClick}
                            onBeehiveReview={handleBeehiveReview}
                        >
                            <QuestActionButton
                                onClick={() => {}}
                                styleType="yellow"
                                width="120px"
                                label="Review"
                            />
                        </ReviewSplitButton>
                    </Attribute>
                )}
                {isPlanning && (
                    <Attribute>
                        <label>&nbsp;</label>
                        <PlanningActions>
                            <QuestActionButton
                                onClick={handleViewPlanningClick}
                                styleType="yellow"
                                width="140px"
                                label="View Planning"
                            />
                            <DeleteQuestButton
                                variant="contained"
                                onClick={handleDeleteClick}
                                disabled={deleting}
                            >
                                Delete Request
                            </DeleteQuestButton>
                        </PlanningActions>
                    </Attribute>
                )}
            </AttributeSection>

            {/* Urgent Priority Dialog */}
            <ConfirmationDialog
                open={urgentPriorityDialogOpen}
                onClose={handleCancelUrgentPriority}
                onConfirm={() => handlePriorityChange(10, true)}
                title="Urgent Request"
                message="You are about to set this request as urgent. This will prioritize it above all other requests. Are you sure you want to proceed?"
            />

            <ConfirmationDialog
                open={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Delete request"
                message="Are you sure you want to delete this request? This will permanently remove the planning session and cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
            />

            <ConfirmationDialog
                open={mergeDialogOpen}
                onClose={handleCancelMerge}
                onConfirm={handleConfirmMerge}
                title="Merge request"
                message="Are you sure you want to merge this request?"
                confirmText="Yes"
                cancelText="No"
            />

            {/* Priority Info Modal */}
            <PriorityInfoModal
                open={priorityInfoModalOpen}
                onClose={() => setPriorityInfoModalOpen(false)}
            />

            <BeehiveReviewModal
                open={beehiveModalOpen}
                questId={quest.id}
                onClose={() => setBeehiveModalOpen(false)}
                onSuccess={handleBeehiveModalSuccess}
            />

            {/* Resume Dialog */}
            <Dialog
                open={resumeDialogOpen}
                onClose={handleCancelResume}
                aria-labelledby="resume-dialog-title"
                aria-describedby="resume-dialog-description"
                PaperProps={ResumeDialogPaperProps}
            >
                <DialogTitle id="resume-dialog-title">
                    Return to development
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="resume-dialog-description">
                        You are about to restart the development. Please confirm
                        this action by clicking the Yes button below
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <NoButton onClick={handleCancelResume}>No</NoButton>
                    <YesButton onClick={handleConfirmResume}>Yes</YesButton>
                </DialogActions>
            </Dialog>

            {/* Pause Dialog */}
            <Dialog
                open={pauseDialogOpen}
                onClose={handleCancelPause}
                aria-labelledby="pause-dialog-title"
                aria-describedby="pause-dialog-description"
                PaperProps={PauseDialogPaperProps}
            >
                <DialogTitle id="pause-dialog-title">Pause request</DialogTitle>
                <DialogContent>
                    <DialogContentText id="pause-dialog-description">
                        Are you sure you want to pause this request? Development
                        won't continue unless you choose to resume
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <NoButton onClick={handleCancelPause}>No</NoButton>
                    <YesButton onClick={handleConfirmPause}>Yes</YesButton>
                </DialogActions>
            </Dialog>

            {/* Archive Dialog */}
            <Dialog
                open={archiveDialogOpen}
                onClose={handleCancelArchive}
                aria-labelledby="archive-dialog-title"
                aria-describedby="archive-dialog-description"
                PaperProps={ArchiveDialogPaperProps}
            >
                <DialogTitle id="archive-dialog-title">
                    {`${
                        archiveType === 'archive' ? 'Archive' : 'Unarchive'
                    } request`}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="archive-dialog-description">
                        {`Are you sure you want to ${archiveType} this request? This
                        action will move the request to ${
                            archiveType === 'archive'
                                ? 'archived'
                                : "it's previous"
                        } status.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <NoButton onClick={handleCancelArchive}>No</NoButton>
                    <YesButton onClick={handleConfirmArchive}>Yes</YesButton>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default WorkOverview;
